import React, { useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { useIsMobile } from '@/shared/hooks';
import { ActionSheet, type ActionSheetItem } from './ActionSheet';
import { Tooltip } from './Tooltip';

/**
 * CardActionsMenu — menu kebab (•••) de ações de card/linha do DS.
 *
 * Desktop: dropdown Radix (`@radix-ui/react-dropdown-menu`) — portal, posição
 * ancorada (align end), teclado/aria/foco nativos (F78-F79). Visual idêntico ao
 * desenho original: w-44, rounded-xl, tema claro fixo, sombra var(--shadow-dropdown).
 * Mobile: fallback ActionSheet (bottom sheet), inalterado.
 */

export interface CardAction {
  label: string;
  icon?: IconName;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  /**
   * Por que a ação está desabilitada — vira Tooltip no item (desktop) quando
   * `disabled` é `true`. Existe porque item desabilitado é `pointer-events-none`
   * (regra do Radix): sem um invólucro que receba o hover, o usuário vê a ação
   * cinza e nenhuma explicação. Ignorado no mobile (ActionSheet não tem hover)
   * e ignorado quando a ação está habilitada.
   */
  disabledReason?: string;
  /** Renderiza um separador (linha) acima deste item — mockup pede antes de "Excluir". */
  dividerBefore?: boolean;
  /**
   * `data-testid` DESTE item, no desktop e na folha inferior do celular. Sem
   * ele o item herda o posicional `${data-testid do menu}-item-${i}`, que muda
   * assim que qualquer ação entra ou sai do menu.
   */
  testId?: string;
}

export interface CardActionsMenuProps {
  actions: CardAction[];
  /** Size of the trigger button */
  size?: 'sm' | 'md';
  /** data-testid applied to the trigger button */
  'data-testid'?: string;
  /**
   * Classes extras NO BOTÃO do gatilho (nunca no conteúdo portalado). Existe
   * para o canvas do React Flow: sem `nodrag nopan` no próprio botão, abrir o
   * menu arrasta o nó e ainda o deixa selecionado — a seleção acontece no
   * `mousedown` do arrasto, onde o `stopPropagation` do React não chega.
   */
  triggerClassName?: string;
  /**
   * Cores do gatilho. `'light'` (padrão) mantém o hex fixo de superfície clara
   * que todos os cards usam hoje. `'theme'` usa `var(--theme-*)` — obrigatório
   * sobre superfície que acompanha o tema (o nó do canvas), onde o hover claro
   * pintaria um bloco branco no tema escuro.
   */
  triggerTone?: 'light' | 'theme';
}

/**
 * Envolve o item num Tooltip SÓ quando há motivo a explicar. O invólucro é um
 * `<span className="block">` porque o `Item` desabilitado do Radix é
 * `pointer-events-none` — o hover precisa chegar em alguém, e o span pai é
 * quem sobra. Sem motivo, devolve o filho intocado (zero mudança na árvore
 * para todos os consumidores que já existiam).
 */
const MaybeTooltip: React.FC<{ reason?: string; children: React.ReactNode }> = ({ reason, children }) =>
  reason ? (
    <Tooltip content={reason} side="left">
      <span className="block">{children}</span>
    </Tooltip>
  ) : (
    <>{children}</>
  );

export const CardActionsMenu: React.FC<CardActionsMenuProps> = ({
  actions,
  size = 'md',
  'data-testid': dataTestId,
  // Renomeado na entrada: `triggerClassName` já é o nome da variável interna
  // que monta as classes do botão, e renomear a de dentro seria refactor à toa.
  triggerClassName: extraTriggerClassName,
  triggerTone = 'light',
}) => {
  // Estado só usado pelo fallback mobile (ActionSheet); no desktop o Radix gerencia.
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = useIsMobile();

  // Trigger idêntico nos dois modos (paridade visual desktop/mobile).
  // As cores passam por TRÊS caminhos (estilo inicial + os dois manipuladores
  // de hover, que escrevem direto em `currentTarget.style`): o tom novo tem de
  // trocar os três, não só a classe.
  const isThemeTone = triggerTone === 'theme';
  const triggerIdleColor = isThemeTone ? 'var(--theme-card-muted)' : '#64748b';
  const triggerHoverBackground = isThemeTone ? 'var(--theme-button-ghost-hover)' : '#f1f5f9';
  const triggerHoverColor = isThemeTone ? 'var(--theme-text)' : '#1e293b';
  const triggerClassName = `rounded-lg transition-colors ${size === 'sm' ? 'p-1' : 'p-1.5'}${
    extraTriggerClassName ? ` ${extraTriggerClassName}` : ''
  }`;
  const triggerStyle: React.CSSProperties = { color: triggerIdleColor };
  const handleTriggerMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = triggerHoverBackground;
    e.currentTarget.style.color = triggerHoverColor;
  };
  const handleTriggerMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent';
    e.currentTarget.style.color = triggerIdleColor;
  };
  const triggerIcon = (
    <Icon name="more-vertical" className={size === 'sm' ? 'w-4 h-4' : 'w-[18px] h-[18px]'} />
  );

  // Mobile: bottom sheet (como está — paridade das ações do dropdown)
  if (isMobile) {
    const sheetItems: ActionSheetItem[] = actions.map((a, i) => ({
      key: String(i),
      label: a.label,
      icon: a.icon,
      color: a.variant === 'danger' ? '#ef4444' : undefined,
      // Paridade com o Radix Item desktop — sem isso, mutation pendente
      // (`disabled: loading`) seguia clicável no sheet mobile.
      disabled: a.disabled,
      // Mesmo id nos dois modos: o teste não precisa saber se a viewport é de
      // celular para achar a ação.
      testId: a.testId,
    }));

    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSheetOpen(!sheetOpen);
          }}
          className={triggerClassName}
          style={triggerStyle}
          data-testid={dataTestId}
          onMouseEnter={handleTriggerMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
        >
          {triggerIcon}
        </button>
        <ActionSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          items={sheetItems}
          onSelect={(key) => { setSheetOpen(false); actions[Number(key)]?.onClick(); }}
        />
      </>
    );
  }

  // Desktop: dropdown Radix ancorado (teclado/aria/foco de graça)
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          // stopPropagation: o kebab vive dentro de cards clicáveis — o clique não pode navegar o card.
          onClick={(e) => e.stopPropagation()}
          className={triggerClassName}
          style={triggerStyle}
          data-testid={dataTestId}
          onMouseEnter={handleTriggerMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
        >
          {triggerIcon}
        </button>
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={4}
          className="w-44 rounded-xl z-[101] py-1 outline-none"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: 'var(--shadow-dropdown)',
          }}
          // Portal do React propaga eventos pela árvore React — barra o clique de chegar no card.
          onClick={(e) => e.stopPropagation()}
          // E o DUPLO clique também: no editor de workflows o duplo clique no
          // quadro abre o seletor de nós. Sem esta linha, um clique impaciente
          // em "Excluir" abre a confirmação E o seletor atrás dela.
          onDoubleClick={(e) => e.stopPropagation()}
          data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
        >
          {actions.map((action, i) => (
            <React.Fragment key={i}>
              {action.dividerBefore && i > 0 && (
                <DropdownMenuPrimitive.Separator
                  className="my-1 mx-2"
                  style={{ borderTop: '1px solid #e5e7eb' }}
                />
              )}
              <MaybeTooltip reason={action.disabled ? action.disabledReason : undefined}>
                <DropdownMenuPrimitive.Item
                  disabled={action.disabled}
                  onSelect={() => action.onClick()}
                  className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm cursor-pointer select-none outline-none transition-colors data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed data-[disabled]:pointer-events-none ${
                    action.variant === 'danger'
                      ? 'data-[highlighted]:bg-[#fef2f2]'
                      : 'data-[highlighted]:bg-[#f1f5f9]'
                  }`}
                  style={{
                    color: action.variant === 'danger' ? '#ef4444' : '#1e293b',
                  }}
                  data-testid={action.testId ?? (dataTestId ? `${dataTestId}-item-${i}` : undefined)}
                >
                  {action.icon && <Icon name={action.icon} className="w-4 h-4 flex-shrink-0" />}
                  {action.label}
                </DropdownMenuPrimitive.Item>
              </MaybeTooltip>
            </React.Fragment>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
};

CardActionsMenu.displayName = 'CardActionsMenu';
